import * as XLSX from 'xlsx';
import { Box, Stack, Typography, Paper, Button, Chip, Divider, Grid, Table, TableBody, TableRow, TableCell, Tooltip } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DownloadIcon from '@mui/icons-material/Download';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { resetWizard } from '../../../../redux/pricingWizardSlice';
import { formatDate, daysFromToday, SLAB_TEMPLATES } from '../helpers/rateEngine';

const AUDIT_LABELS = {
  commercial_construct: 'Commercial construct',
  tcs_blended: 'Serviceability & guardrails',
  consolidated_review: 'Consolidated review',
  parent___blended_surface__: 'Blended rate card · Surface',
  parent___blended_air__: 'Blended rate card · Air',
  parent___blended_sdd_ndd__: 'Blended rate card · SDD / NDD',
  commit_to_proship: 'Commit to ProShip',
};
function auditLabel(k) {
  if (AUDIT_LABELS[k]) return AUDIT_LABELS[k];
  if (k.startsWith('rvp_parent_')) return 'RVP rate card · ' + k.slice(11).replace(/_/g, ' ');
  if (k.startsWith('parent_')) return 'Courier rate card · ' + k.slice(7).replace(/_/g, ' ');
  return k;
}

// ── Excel builders ────────────────────────────────────────────────────────────

function downloadRateCardExcel(rows, merchantBrand, version) {
  if (!rows?.length) return;
  const wb = XLSX.utils.book_new();
  const parents = [...new Set(rows.map((r) => r.parent).filter(Boolean))];
  parents.forEach((parent) => {
    const pRows = rows.filter((r) => r.parent === parent);
    const wsData = [
      ['Account Code', 'Zone', 'Min Weight (GMS)', 'Max Weight (GMS)', 'Price (₹)', 'Tax (%)', 'RTO (% of fwd)', 'Service', 'Mode'],
      ...pRows.map((r) => [r.account_code, `Zone ${r.zone}`, r.start, r.end, r.price, r.tax, r.rto_pct, r.service || '', r.mode || '']),
    ];
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    ws['!cols'] = [{ wch: 26 }, { wch: 8 }, { wch: 16 }, { wch: 16 }, { wch: 10 }, { wch: 8 }, { wch: 14 }, { wch: 14 }, { wch: 10 }];
    XLSX.utils.book_append_sheet(wb, ws, parent.slice(0, 31));
  });
  XLSX.writeFile(wb, `Base_Rate_Card_${merchantBrand}_${version}.xlsx`);
}


function downloadPreflightReport(preflight, merchantBrand) {
  if (!preflight) return;
  const lines = [
    `Pre-flight Report — ${merchantBrand}`,
    `Generated at: ${new Date().toLocaleString()}`,
    `Result: ${preflight.ok ? 'PASS ✓' : 'FAIL ✗'}`,
    '',
    'Summary',
    `  Account codes : ${preflight.summary?.accountCodes ?? '—'}`,
    `  Rate-card rows: ${preflight.summary?.rateCardRows ?? '—'}`,
    `  Effective mode: ${preflight.summary?.effectiveMode ?? '—'}`,
    '',
    preflight.issues?.length ? `Issues (${preflight.issues.length})` : 'No issues found.',
    ...(preflight.issues || []).map((i) => `  [${i.acc}] Zone ${i.zone ?? '?'}: ${i.msg}`),
  ];
  const blob = new Blob([lines.join('\n')], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `Preflight_Report_${merchantBrand}.txt`;
  a.click();
  URL.revokeObjectURL(url);
}

function downloadSetupMemo(s, currentUser) {
  const lines = [
    '═══════════════════════════════════════════════════',
    '        MERCHANT PRICING SETUP — CONFIRMATION MEMO',
    '═══════════════════════════════════════════════════',
    '',
    `Merchant    : ${s.merchant?.brand || '—'} (${s.merchant?.code || '—'})`,
    `Completed by: ${currentUser}`,
    `Committed at: ${s.commit?.ts || '—'}`,
    '',
    'RATE CARD',
    `  Version        : ${s.commit?.version || '—'}`,
    `  Account codes  : ${s.commit?.written || '—'}`,
    `  Rows           : ${s.commit?.rows || '—'}`,
    `  Effective date : ${formatDate(s.effective_date)} (${s.effective_mode})`,
    '',
    'SERVICE TYPES',
    ...(s.resolvedServices || []).map((x) => `  • ${x.id}`),
    '',
    'CONTRACT TERMS',
    `  GST stance      : ${s.agreement?.gst_stance || '—'}`,
    `  Weight ceiling  : ${s.agreement?.weight_ceiling || '—'} GMS`,
    `  Invoice ceiling : ₹${s.invoice_ceiling || '—'}`,
    `  Volumetric coeff: ${s.agreement?.vc || '—'}`,
    `  Slab template   : ${SLAB_TEMPLATES.find((t) => t.id === s.rateCard?.slabTemplate)?.label || '—'}`,
    '',
    'RVP',
    `  Enabled : ${s.rvp?.enabled ? 'Yes' : 'No'}`,
    `  Mode    : ${s.rvp?.mode || '—'}`,
    '',
    'AUDIT TRAIL',
    ...Object.entries(s.confirmations || {}).map(([k, v]) => `  • ${auditLabel(k)} — ${v.by} at ${new Date(v.at).toLocaleString()}`),
    '',
    '═══════════════════════════════════════════════════',
  ];
  const blob = new Blob([lines.join('\n')], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `Setup_Memo_${s.merchant?.brand || 'merchant'}_${s.commit?.version || 'v1'}.txt`;
  a.click();
  URL.revokeObjectURL(url);
}

// ── Artifact row component ────────────────────────────────────────────────────

function ArtifactRow({ icon, label, description, onDownload, disabled, badge }) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1.5}
      sx={{
        py: 1.25,
        px: 1.5,
        borderRadius: 1,
        border: (t) => `1px solid ${t.palette.divider}`,
        bgcolor: disabled ? 'action.hover' : 'background.paper',
        opacity: disabled ? 0.55 : 1,
      }}
    >
      <Typography sx={{ fontSize: 20, lineHeight: 1 }}>{icon}</Typography>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" alignItems="center" spacing={0.75}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>{label}</Typography>
          {badge && <Chip size="small" label={badge} sx={{ height: 18, fontSize: 10, fontWeight: 600 }} />}
        </Stack>
        <Typography variant="caption" color="text.secondary">{description}</Typography>
      </Box>
      <Tooltip title={disabled ? 'No data available for this artifact' : `Download ${label}`}>
        <span>
          <Button
            size="small"
            variant="outlined"
            startIcon={<DownloadIcon sx={{ fontSize: 14 }} />}
            onClick={onDownload}
            disabled={disabled}
            sx={{ whiteSpace: 'nowrap', minWidth: 110 }}
          >
            Download
          </Button>
        </span>
      </Tooltip>
    </Stack>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function Stage8_Done() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const s = useSelector((st) => st.pricingWizard);
  const currentUser = useSelector((st) => st.user?.userInfo?.fullname || st.user?.userInfo?.name || st.user?.userInfo?.sub || 'Admin');
  const c = s.commit;

  if (!c) {
    return <Box><Typography variant="body2" color="text.secondary">No commit yet — go back to Stage 8 (Pre-flight & Commit) and run pre-flight, then commit.</Typography></Box>;
  }

  const liveTxt = s.effective_mode === 'immediate'
    ? <>live <b>now</b> (effective {formatDate(s.effective_date)})</>
    : <>scheduled to go live on <b>{formatDate(s.effective_date)}</b> ({daysFromToday(s.effective_date)}) — old rate card remains active until then</>;

  const merchantBrand = s.merchant?.brand || 'merchant';
  const version = c.version || 'v1';

  const fwdRows = s.rateCard?.rows || [];
  const auditKeys = Object.keys(s.confirmations || {});

  const agreementSrc = s.agreement?.pdfDataUrl || s.agreement?.s3Url || null;
  const fwdParentCount = [...new Set(fwdRows.map((r) => r.parent).filter(Boolean))].length;

  const artifacts = [
    {
      icon: '📊',
      label: 'Base Rate Card.xlsx',
      description: fwdRows.length > 0
        ? `Forward rate card — ${fwdParentCount} courier(s) · ${fwdRows.length} rows · one sheet per courier`
        : `Forward rate card — committed ${c.rows} rows (session data cleared; re-export from merchant profile)`,
      disabled: fwdRows.length === 0,
      badge: fwdRows.length > 0 ? `${fwdRows.length} rows` : `${c.rows} rows committed`,
      onDownload: () => downloadRateCardExcel(fwdRows, merchantBrand, version),
    },
    {
      icon: '📎',
      label: s.agreement?.filename || 'Signed Agreement.pdf',
      description: agreementSrc
        ? 'Uploaded commercial agreement PDF — signed copy on record'
        : 'Agreement PDF not available in this session',
      disabled: !agreementSrc,
      badge: agreementSrc ? 'PDF' : null,
      onDownload: () => {
        if (!agreementSrc) return;
        const a = document.createElement('a');
        a.href = agreementSrc;
        a.download = s.agreement?.filename || 'Signed_Agreement.pdf';
        a.target = '_blank';
        a.click();
      },
    },
    {
      icon: '✅',
      label: 'Pre-flight Report.txt',
      description: s.preflight
        ? `${s.preflight.ok ? 'All checks passed' : `${s.preflight.issues?.length ?? 0} issue(s) found`} — ${s.preflight.summary?.rateCardRows ?? c.rows} rows validated`
        : 'Run pre-flight on Stage 7 to generate this report',
      disabled: !s.preflight,
      badge: s.preflight?.ok ? 'PASS' : s.preflight ? 'FAIL' : null,
      onDownload: () => downloadPreflightReport(s.preflight, merchantBrand),
    },
    {
      icon: '📋',
      label: 'Setup Confirmation Memo.txt',
      description: `Complete setup summary — merchant, rate card, service types, contract terms, audit trail`,
      disabled: false,
      badge: null,
      onDownload: () => downloadSetupMemo(s, currentUser),
    },
  ];

  return (
    <Box>
      {/* Success banner */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'success.lighter', borderColor: 'success.light', display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <CheckCircleIcon sx={{ fontSize: 32, color: 'success.main' }} />
        <Typography variant="body2">
          Rate card committed to ProShip · version <b>{c.version}</b> · <b>{c.written}</b> account codes · <b>{c.rows}</b> rows · {liveTxt} · committed at {c.ts}
        </Typography>
      </Paper>

      {/* Artifacts */}
      <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.25 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>📁 Artifacts</Typography>
          <Chip size="small" label={`${artifacts.filter((a) => !a.disabled).length} available`} color="success" variant="outlined" sx={{ height: 18, fontSize: 10, fontWeight: 600 }} />
        </Stack>
        <Stack spacing={1}>
          {artifacts.map((a) => (
            <ArtifactRow key={a.label} {...a} />
          ))}
        </Stack>
      </Paper>

      {/* Email dispatched */}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>📧 Email dispatched</Typography>
        <Chip size="small" color="info" variant="outlined" label="config in merchant_config.pricingSetupEmail" />
      </Stack>
      <Paper variant="outlined" sx={{ p: 1.5, bgcolor: '#fff', mb: 2 }}>
        <Box component="pre" sx={{ m: 0, fontFamily: 'JetBrains Mono, monospace', fontSize: 11.5, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
          {`Completed by : ${currentUser}
Merchant     : ${merchantBrand} (${s.merchant?.code || '—'}) from the ProShip admin panel.

  • Rate card version : ${c.version}
  • Effective date    : ${formatDate(s.effective_date)} ${s.effective_mode === 'scheduled' ? '(scheduled)' : '(immediate)'}
  • Service types     : ${s.resolvedServices.map((x) => x.id).join(', ')}
  • Account codes     : ${c.written}
  • GST stance        : ${s.agreement.gst_stance}

Attachments
  • Base Rate Card.xlsx (per-courier tabs)${s.rvp?.enabled ? '\n  • RVP Rate Card.xlsx' : ''}
  • Setup Confirmation Memo.txt
  • Pre-flight Report.txt

Regards,
ProShip Admin Panel`}
        </Box>
      </Paper>

      {/* Summary */}
      <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>📊 Summary</Typography>
        <Table size="small">
          <TableBody>
            {[
              ['Merchant', `${s.merchant?.brand} (${s.merchant?.code})`],
              ['Service types', s.resolvedServices.map((x) => x.id).join(', ')],
              ['Account codes', String(c.written)],
              ['GST stance', s.agreement.gst_stance],
              ['Slab template', SLAB_TEMPLATES.find((t) => t.id === s.rateCard.slabTemplate)?.label || '—'],
              ['Weight ceiling', `${s.agreement.weight_ceiling || '—'} GMS`],
              ['Invoice ceiling', `₹${s.invoice_ceiling || '—'}`],
              ['Rate card version', c.version],
              ['Effective date', `${formatDate(s.effective_date)} · ${s.effective_mode}`],
              ['Operator', currentUser],
            ].map(([k, v]) => (
              <TableRow key={k}>
                <TableCell sx={{ border: 0, py: 0.4, fontWeight: 700, fontSize: 12, color: 'text.secondary', width: 160 }}>{k}</TableCell>
                <TableCell sx={{ border: 0, py: 0.4, fontSize: 12.5 }}>{v}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      {/* Audit trail */}
      {auditKeys.length > 0 && (
        <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            ✓ Audit trail{' '}
            <Chip size="small" color="success" label={`${auditKeys.length} confirmation${auditKeys.length > 1 ? 's' : ''}`} sx={{ ml: 0.5, height: 18 }} />
          </Typography>
          <Table size="small">
            <TableBody>
              {auditKeys.map((k) => {
                const ct = s.confirmations[k];
                const dt = new Date(ct.at).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
                return (
                  <TableRow key={k}>
                    <TableCell sx={{ border: 0, py: 0.4, fontWeight: 700, fontSize: 12 }}>{auditLabel(k)}</TableCell>
                    <TableCell sx={{ border: 0, py: 0.4, fontSize: 12, color: 'text.secondary' }}>{ct.by} · {dt}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Paper>
      )}

      <Divider sx={{ my: 2 }} />
      <Stack direction="row" spacing={1.5} justifyContent="center" flexWrap="wrap" useFlexGap>
        <Button variant="contained" onClick={() => navigate(`/merchant-config/tat-setup/${s.merchantId}`)}>Continue to Step 2 · TAT Setup</Button>
        <Button variant="outlined" onClick={() => navigate('/merchant-config/pricing-setup/list')}>Back to merchant list</Button>
        <Button variant="text" color="inherit" onClick={() => { dispatch(resetWizard()); navigate('/merchant-config/pricing-setup/list'); }}>Onboard another merchant</Button>
      </Stack>
    </Box>
  );
}
